<template>
  <v-card class="profile-selection__card" height="475" :min-width="smAndUp ? '450' : '300'">
    <v-card-title>
      <v-row justify="center" v-if="illustrationLight" class="profile-selection__illustration ma-0">
        <v-col cols="auto" class="pb-0">
          <img
            :src="illustrationLight"
            :alt="$t('alt.profileSelectionIllustration')"
            height="128px"
          />
        </v-col>
      </v-row>
      <p class="profile-selection__user d-block text-center text-wrap text-h5 pb-3 text-primary">
        {{
          $t('messages.welcomeBackUser', {
            user: user?.firstName || user?.loginName,
          })
        }}
      </p>
    </v-card-title>
    <v-card-text
      :class="[
        'd-flex mx-2 mb-2 pb-0 overflow-auto',
        businessProfiles.length > 3 ? 'align-start' : 'align-center',
      ]"
    >
      <v-container v-if="businessProfiles.length == 0" class="d-flex justify-center">
        <v-alert
          type="info"
          class="profile-selection__no-bps-alert"
          variant="outlined"
          max-width="300"
          :text="$t('messages.noBpsAssigned')"
        ></v-alert>
      </v-container>
      <v-container v-else class="pa-0 bp-list">
        <v-text-field
          v-model="searchInput"
          clearable
          rounded="0"
          block
          density="compact"
          variant="outlined"
          hide-details
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('labels.searchForYourProfiles')"
          v-if="businessProfiles.length > 3"
        ></v-text-field>
        <v-list
          class="profile-selection__bp-list pa-0"
          max-height="198"
          :width="smAndUp ? '400' : '250'"
        >
          <v-list-item
            v-for="businessProfile in filterBusinessProfiles"
            :key="businessProfile.id"
            @click="confirmBusinessProfile(businessProfile)"
            :border="true"
            class="pa-3"
          >
            <template #prepend>
              <v-avatar
                v-if="businessProfile.thumbnailLogo"
                class="profile-selection__bp-list__img"
              >
                <v-img :src="logoStoragePath + businessProfile.thumbnailLogo" />
              </v-avatar>
              <v-avatar
                v-else
                color="primary"
                class="profile-selection__bp-list__avatar"
                rounded="0"
              >
                <span>{{ getStoreNameInitials(businessProfile.name) }}</span>
              </v-avatar>
            </template>
            <v-list-item-title class="profile-selection__bp-list__name">
              {{ businessProfile.name }}
            </v-list-item-title>
            <template #append>
              <v-progress-circular
                v-if="loading && businessProfileId == businessProfile.id"
                indeterminate
                color="primary"
                class="profile-selection__bp-list__loading"
              ></v-progress-circular>
              <v-icon v-else>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
          <v-list-item
            v-show="filterBusinessProfiles.length == 0"
            :border="true"
            class="text-center pa-5 text-body-1"
          >
            {{ $t('messages.noProfilesFoundForYourSearch') }}
          </v-list-item>
        </v-list>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

import { useAppStore } from '@/stores/app'
import { completeLoginWithBusinessProfile } from '@/router/authGuards'
import appConfig from '@/constants/appConfig'
import type { BusinessProfile } from '@/types/businessProfile'

const appStore = useAppStore()
const router = useRouter()
const route = useRoute()
const { smAndUp } = useDisplay()
const illustrationLight = appConfig.PROFILE_SELECTION_ILLUSTRATION_LIGHT
const logoStoragePath = appConfig.LOGO_STORAGE_PATH

const user = ref(appStore.user)
const loading = ref(false)
const searchInput = ref()
const businessProfileId = ref()

const businessProfiles = computed(() => appStore.businessProfiles)

const filterBusinessProfiles = computed((): BusinessProfile[] => {
  if (searchInput.value && searchInput.value.trim().length) {
    return businessProfiles.value.filter((bp: BusinessProfile) =>
      bp.name.toLowerCase().includes(searchInput.value.toLowerCase()),
    )
  }
  return businessProfiles.value
})

const confirmBusinessProfile = async (businessProfile: BusinessProfile) => {
  loading.value = true
  businessProfileId.value = businessProfile.id
  const redirect =
    typeof route.query.redirect === 'string' ? route.query.redirect : null
  const destination = await completeLoginWithBusinessProfile(businessProfile.id, redirect)
  await router.push(destination)
}

const getStoreNameInitials = (name: string) => {
  if (name) {
    const storeNames = name.split(' ')
    if (storeNames.length < 2) {
      return storeNames[0].slice(0, 2)
    } else {
      return storeNames
        .slice(0, 2)
        .map((n: string) => n[0])
        .join('')
    }
  }
}

</script>

<style scoped>
.profile-selection__card {
  display: flex;
  flex-flow: column;
  height: 100%;
}

.bp-list {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  max-width: 500px;
  border: 0.1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.profile-selection__user {
  max-width: 418px;
}
</style>
